<template>
  <b-row align-v="center" align-h="center" class="h-100 pb-5">
    <b-col cols="auto" sm="6" md="6" lg="4" xl="3">
      <div>
        <landing-logo size="60px" class="mb-4" />

        <h5 class="display-4 mt-1 mb-2 font-weight-bold">Reset your password</h5>

        <p class="text-black-50">
          Please enter your email and we'll send you instructions on how to reset your password.
        </p>
      </div>

      <b-alert
        v-if="isSendSuccess"
        ref="send-instructor-success-alert"
        show
        class="d-flex align-items-center pl-2 align-content-center"
        variant="success"
      >
        <div class="px-2">
          An email with password reset instructions has been sent to your email address. Email
          messages may take a few minutes to arrive.
        </div>
      </b-alert>

      <b-alert
        v-else-if="isGoogleAccountError"
        ref="google-account-error-alert"
        show
        class="d-flex align-items-center pl-2 align-content-center"
        variant="warning"
      >
        <div class="px-2">
          {{ errorMessage }}
        </div>
      </b-alert>

      <div v-else class="mt-4">
        <b-form ref="form-forgot-password" name="form-forgot-password" @submit.prevent="onSubmit">
          <b-form-group label="Email">
            <b-form-input
              id="email"
              ref="email-input"
              v-model="email"
              name="email"
              type="text"
              class="mb-1"
              :formatter="emailFormatter"
              :class="{ 'invalid-input': error }"
              placeholder="Enter your email"
              :state="$v.email.$dirty ? !$v.email.$error : null"
              @change="onInputChange"
            />

            <b-form-invalid-feedback v-if="!$v.email.required" ref="text__password-required">
              Email is required
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-else-if="!$v.email.email"
              ref="text__password-invalid-format"
            >
              Email is invalid format
            </b-form-invalid-feedback>

            <div v-else-if="error" ref="error-message" class="invalid-text">
              {{ errorMessage }}
            </div>
          </b-form-group>

          <b-button
            ref="send-instructor-button"
            type="submit"
            class="w-100 btn__reset-password mt-2"
            variant="primary"
          >
            <b-spinner v-if="submitting"></b-spinner>

            <span v-else>Send instructions</span>
          </b-button>
        </b-form>
      </div>

      <p class="text-black-50 mt-4">
        Know your password?

        <router-link class="link__to-sign-in" :to="signInPage">Try Signing in</router-link>
      </p>
    </b-col>
  </b-row>
</template>

<script>
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import { required, email } from 'vuelidate/lib/validators';
import Utility from '@/models/Utility';
import LandingLogo from '@/components/layout/LandingLogo.vue';
import { useInputFormatter } from '@/composables/useInputFormatter';

const { email: emailFormatter } = useInputFormatter();

export default {
  components: {
    LandingLogo
  },

  mixins: [amplitudeMixin],

  data() {
    return {
      email: '',
      submitting: false,
      isSendSuccess: false,
      isGoogleAccountError: false,
      error: false,
      errorMessage: ''
    };
  },

  validations() {
    return {
      email: {
        required,
        email
      }
    };
  },

  computed: {
    signInPage() {
      return { name: 'SignIn', query: this.$route.query };
    }
  },

  methods: {
    emailFormatter,

    async onSubmit() {
      this.$v.email.$touch();

      if (this.$v.email.$anyError) return;

      this.isSendSuccess = false;
      this.isGoogleAccountError = false;
      this.errorMessage = '';
      this.submitting = true;

      try {
        await Utility.api().requestResetPassword({
          email: this.$v.email.$model
        });

        this.isSendSuccess = true;
      } catch (error) {
        console.log('error', error);
        this.errorMessage = error.response?.data?.message || 'An error occurred';

        // Check if it's a Google account error (show as alert instead of inline error)
        if (this.errorMessage.includes('Google sign-in')) {
          this.isGoogleAccountError = true;
          this.error = false;
        } else {
          this.error = true;
          this.isGoogleAccountError = false;
        }
      } finally {
        this.submitting = false;

        let eventBody = {
          isSendSuccess: this.isSendSuccess,
          Message: this.errorMessage
        };

        this.$_amplitudeMixin_onCheck(eventBody);
      }
    },

    onInputChange() {
      this.error = false;
      this.isGoogleAccountError = false;
      this.errorMessage = '';
    }
  }
};
</script>

<style lang="scss" scoped>
$blue: #4191ff;

.btn__reset-password {
  color: #fff;
  background-color: $blue;
  border-color: $blue;
  border: 2px solid $blue;
  box-shadow: 0px 3.52px 8.4px rgba(65, 145, 255, 0.4), 0px 1px 6.16px rgba(65, 145, 255, 0.54);
  border-radius: 3.2px;
}

.form-control.is-invalid {
  background-image: none !important;
}

.form-control.is-valid {
  background-image: none !important;
}

.invalid-text {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}

.invalid-input {
  border-color: #f83245;
}
</style>
